import React, { lazy, Suspense, useEffect } from "react";
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from "react-router-dom";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import Home from "./pages/Home";
import ScrollToTop from "./components/ScrollToTop";
import DelayedPopup from "./components/DelayedPopup";
import { captureAttributionOnLanding } from "./util/attribution";

const Checkout = lazy(() => import("./pages/Checkout"));
const ThankYou = lazy(() => import("./pages/ThankYou"));
const Contact = lazy(() => import("./pages/Contact"));
const About = lazy(() => import("./pages/About"));
const Faq = lazy(() => import("./pages/Faq"));
const StrategyDetail = lazy(() => import("./components/StrategyDetail"));
const TradingPlatform = lazy(() => import("./pages/TradingPlatform"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const RiskDisclosure = lazy(() => import("./pages/RiskDisclosure"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const ReturnPolicy = lazy(() => import("./pages/ReturnPolicy"));
const Affiliates = lazy(() => import("./pages/Affiliates"));
const BlogList = lazy(() => import("./components/BlogList"));
const BlogPost = lazy(() => import("./components/BlogPost"));
const PortfolioAnalyzer = lazy(() => import("./pages/PortfolioAnalyzer"));
const QuantPortfolio = lazy(() => import("./pages/QuantPortfolio"));
const CustomFormPage = lazy(() => import("./pages/FreeAlgoFormPage"));
const BookDemo = lazy(() => import("./pages/BookDemo"));
const BookProp = lazy(() => import("./pages/BookProp"));
const BookBrokerage = lazy(() => import("./pages/BookBrokerage"));
const BookVSL = lazy(() => import("./pages/BookVSL"));
const DemoBookingThankYou = lazy(() => import("./pages/DemoBookingThankYou"));
const PaymentPlanCheckout = lazy(() => import("./pages/PaymentPlanCheckout"));
const PaymentPlanCheckoutTest = lazy(() => import("./pages/PaymentPlanCheckoutTest"));
const PayoutSimulator = lazy(() => import("./pages/PayoutSimulator"));
const SalesLogin = lazy(() => import("./pages/SalesLogin"));
const SalesDashboard = lazy(() => import("./pages/SalesDashboard"));
const Schedule = lazy(() => import("./pages/Schedule"));
const PortfolioBuilderLogin = lazy(() => import("./pages/PortfolioBuilderLogin"));
const PortfolioBuilder = lazy(() => import("./pages/PortfolioBuilder"));
const PortfolioQuiz = lazy(() => import("./pages/PortfolioQuiz"));
const Portal = lazy(() => import("./pages/Portal"));
const PerformanceDashboard = lazy(() => import("./pages/PerformanceDashboard"));
const PortalAccessAdmin = lazy(() => import("./pages/PortalAccessAdmin"));

const HIDE_NAV_PREFIXES = ['/tools/', '/sales', '/portal'];
// Standalone, chrome-free routes (exact match). Kept separate from
// HIDE_NAV_PREFIXES because a '/book' prefix would also match '/book-demo'.
const STANDALONE_PATHS = ['/book', '/portfolio-quiz', '/book-demo', '/book-demo/prop', '/book-demo/invest'];
// Tawk: also update showPaths inside Tawk_API.onLoad in public/index.html (must match).
const TAWK_VISIBLE_PATHS = [
  '/',
  '/strategies',
  '/book',
  '/portfolio-quiz',
  '/book-demo/classic',
  '/book-demo/prop-fees',
  '/portfolio_analyzer',
  '/faq',
];

// Keeps UTM and click-id params attached when an old landing-page slug
// forwards to its current URL, so paid traffic stays attributable.
function RedirectWithQuery({ to }) {
  const { search, hash } = useLocation();
  return <Navigate to={`${to}${search}${hash}`} replace />;
}

function AppContent() {
  const location = useLocation();
  const standalone = STANDALONE_PATHS.includes(location.pathname);
  const hideNav = standalone || HIDE_NAV_PREFIXES.some(p => location.pathname.startsWith(p));

  useEffect(() => {
    const show = TAWK_VISIBLE_PATHS.includes(location.pathname);
    if (window.Tawk_API) {
      if (show) {
        window.Tawk_API.showWidget?.();
      } else {
        window.Tawk_API.hideWidget?.();
      }
    }
  }, [location.pathname]);

  useEffect(() => {
    captureAttributionOnLanding();
  }, [location.pathname, location.search]);

  return (
    <>
      {!standalone && <DelayedPopup delay={30000} />}
      <ScrollToTop />
      {!hideNav && <Navbar />}
      <Suspense fallback={<div className="min-h-screen bg-[#09090b]" aria-label="Loading page" />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/strategies" element={<PortfolioBuilder isPublic={true} />} />
          <Route path="/portfolio-quiz" element={<PortfolioQuiz />} />
          <Route path="/strategies/:slug" element={<StrategyDetail />} />
          <Route path="/platform" element={<TradingPlatform />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/thankyou" element={<ThankYou />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/about" element={<About />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/risk" element={<RiskDisclosure />} />
          <Route path="/return" element={<ReturnPolicy />} />
          <Route path="/affiliates" element={<Affiliates />} />
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/post/:id/*" element={<BlogPost />} />
          <Route path="/portfolios/zta_quant_portfolio" element={<QuantPortfolio />} />
          <Route path="/portfolio_analyzer" element={<PortfolioAnalyzer />} />
          <Route path="/free_guide" element={<CustomFormPage />} />
          <Route path="/book" element={<BookVSL />} />
          {/* Default /book-demo is the investor lander (BookBrokerage). Prop
              lander stays at /book-demo/prop. Classic booking at /book-demo/classic. */}
          <Route path="/book-demo" element={<BookBrokerage />} />
          <Route path="/book-demo/classic" element={<BookDemo />} />
          <Route path="/book-demo/prop" element={<BookProp />} />
          <Route path="/book-demo/invest" element={<BookBrokerage />} />
          {/* Shipped briefly under their own slugs before we consolidated. */}
          <Route path="/book-prop" element={<RedirectWithQuery to="/book-demo/prop" />} />
          <Route path="/book-brokerage" element={<RedirectWithQuery to="/book-demo" />} />
          <Route path="/book-demo/prop-fees" element={<BookDemo />} />
          <Route path="/book-demo-confirmed-thankyou" element={<DemoBookingThankYou />} />
          <Route path="/checkout/payment-plan" element={<PaymentPlanCheckout />} />
          <Route path="/checkout/payment-plan/test" element={<PaymentPlanCheckoutTest />} />
          <Route path="/payout-simulator" element={<PayoutSimulator />} />
          <Route path="/sales" element={<SalesLogin />} />
          <Route path="/sales/dashboard" element={<SalesDashboard />} />
          <Route path="/sales/schedule" element={<Schedule />} />
          <Route path="/tools/portfolio_builder" element={<PortfolioBuilderLogin />} />
          <Route path="/tools/portfolio_builder/dashboard" element={<PortfolioBuilder />} />
          <Route path="/portal" element={<Portal />} />
          <Route path="/portal/dashboard" element={<Navigate to="/portal/performance" replace />} />
          <Route path="/portal/performance" element={<PerformanceDashboard />} />
          <Route path="/portal/admin/access" element={<PortalAccessAdmin />} />
        </Routes>
      </Suspense>
      {!hideNav && <Footer />}
    </>
  );
}

function App() {
  useEffect(() => {
    document.title = "Zion Trading Algos";
  }, []);
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
